/**
 * Generates equidistant filter-stage colors in the OKLCH perceptual colorspace.
 *
 * Each color has four semantic variants (subtle/medium/strong background and
 * foreground text) matching the CSS custom properties consumed by FilterBar
 * pills, HighlightedCell marks, and ColumnFilterPopover trace lines.
 *
 * Hue distribution uses the golden angle (~137.508°) so that:
 * - colours are maximally separated for any N,
 * - adding a new stage doesn't reshuffle existing colours.
 */

/** CSS custom property values for a single filter stage color. */
export interface FilterStageColorVars {
  "--filter-stage-bg-subtle": string;
  "--filter-stage-bg-medium": string;
  "--filter-stage-bg-strong": string;
  "--filter-stage-fg": string;
}

/**
 * Golden angle in degrees — produces maximally-separated hue sequences.
 * For any prefix of the sequence, hues are as far apart as possible.
 */
const GOLDEN_ANGLE = 137.508;

/**
 * Starting hue. 250° gives a blue-ish first color, matching the previous
 * static palette's first entry.
 */
const START_HUE = 250;

function oklch(lightness: number, chroma: number, hue: number): string {
  const l = (lightness * 100).toFixed(1);
  const c = chroma.toFixed(3);
  const h = hue.toFixed(1);
  return `oklch(${l}% ${c} ${h})`;
}

/**
 * Generates color definitions for `count` filter stages.
 *
 * Uses `light-dark()` CSS function so colors adapt to the active color scheme.
 *
 * | Variant    | Light L | Light C | Dark L | Dark C | Purpose                |
 * |------------|---------|---------|--------|--------|------------------------|
 * | bg-subtle  | 0.88    | 0.06   | 0.30   | 0.08   | Pill / cell background |
 * | bg-medium  | 0.82    | 0.08   | 0.36   | 0.10   | Highlight background   |
 * | bg-strong  | 0.76    | 0.09   | 0.42   | 0.11   | Popover trace line     |
 * | fg         | 0.25    | 0.08   | 0.90   | 0.05   | Text on bg             |
 */
export function generateFilterStageColors(
  count: number,
): FilterStageColorVars[] {
  const colors: FilterStageColorVars[] = [];
  for (let i = 0; i < count; i++) {
    const hue = (START_HUE + i * GOLDEN_ANGLE) % 360;
    colors.push({
      "--filter-stage-bg-subtle": `light-dark(${oklch(0.88, 0.06, hue)}, ${oklch(0.3, 0.08, hue)})`,
      "--filter-stage-bg-medium": `light-dark(${oklch(0.82, 0.08, hue)}, ${oklch(0.36, 0.1, hue)})`,
      "--filter-stage-bg-strong": `light-dark(${oklch(0.76, 0.09, hue)}, ${oklch(0.42, 0.11, hue)})`,
      "--filter-stage-fg": `light-dark(${oklch(0.25, 0.08, hue)}, ${oklch(0.9, 0.05, hue)})`,
    });
  }
  return colors;
}

/**
 * Returns a `style` object that sets the filter-stage CSS custom properties
 * for the given color index, wrapping around when index exceeds the palette.
 */
export function stageColorStyle(
  colorIndex: number,
  colors: FilterStageColorVars[],
): React.CSSProperties {
  if (colors.length === 0) {
    return {};
  }
  return colors[colorIndex % colors.length] as unknown as React.CSSProperties;
}
